import { getCurrentTenantRole } from './currentTenantRoleStore';
import { canViewPrices, DEFAULT_PRICE_VISIBLE_FIELD_ROLES } from '../job/jobRecordAccess';

/**
 * Tiny module-level mirror of the CURRENT session's price-visibility inputs
 * (RD-ROLE-005b — field-role dollar-free reads, client wiring).
 *
 * WHY THIS EXISTS: same rationale as the sibling `currentTenantRoleStore.js`.
 * `priceVisibleFieldRoles` normally lives only in `CompanySettingsContext`
 * (React state), which plain (non-React) modules — services, data-loading
 * helpers that aren't hooks — cannot read. Threading a `canSeePrices` param
 * through every call site of a shared read risks the exact footgun this repo
 * has hit before under other names: a hand-threaded key/param list that's
 * easy to miss at ONE call site, which then silently leaks a dollar (or,
 * worse, error-screens) for a blind field role with no test failure and no
 * visible signal (see CLAUDE.md's anti-patterns section on hand-threaded
 * call-site lists). Auto-detecting from a single mirrored source removes
 * that failure mode entirely — every current AND future non-hook caller of a
 * price-aware read gets the right table/view for free.
 *
 * SOURCE OF TRUTH: mirrors `CompanySettingsContext`'s resolved
 * `priceVisibleFieldRoles` state value verbatim — this module never
 * re-derives it independently, so there is exactly ONE place that decides
 * "which field roles can see prices," not two that can drift apart.
 * `CompanySettingsContext` calls `setPriceVisibleFieldRoles` (this store's
 * setter, aliased) at the SAME site it sets its own React state of the same
 * name — both on a successful settings fetch AND on the sign-out reset
 * branch, mirroring the `setRoleMirrored` pattern in `AuthContext.js`.
 *
 * DEFAULT: the DB default `['supervisor']` (worker blind, supervisor
 * visible) applies only when the value is UNKNOWN — before the context has
 * resolved settings, or after a sign-out reset. It is NOT substituted for a
 * legitimately empty array: a tenant CAN blind every field role, including
 * supervisor (`price_visible_field_roles = '{}'`), and that real setting
 * must be stored as `[]` verbatim — coercing it back to `['supervisor']`
 * would silently un-blind a supervisor the tenant deliberately blinded.
 * Only `null`/`undefined`/non-array input (unknown, not "empty on purpose")
 * falls back to the default.
 */

let priceVisibleFieldRoles = DEFAULT_PRICE_VISIBLE_FIELD_ROLES;

/** Called by CompanySettingsContext whenever the resolved setting changes (or resets). */
export function setPriceVisibleFieldRoles(roles) {
  priceVisibleFieldRoles = Array.isArray(roles) ? roles : DEFAULT_PRICE_VISIBLE_FIELD_ROLES;
}

/**
 * Returns the mirrored roles array. May legitimately be `[]` when a tenant
 * has blinded every field role — the DB default `['supervisor']` applies
 * only while the setting is unresolved (see the header comment above).
 */
export function getPriceVisibleFieldRoles() {
  return priceVisibleFieldRoles;
}

/**
 * Convenience predicate for non-hook (non-React) callers: "can THIS session
 * see prices?" — combines the mirrored tenant role with the mirrored
 * price-visibility setting via the same pure predicate hooks use.
 */
export function canCurrentSessionSeePrices() {
  return canViewPrices(getCurrentTenantRole(), priceVisibleFieldRoles);
}
